import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {forkJoin, Observable} from 'rxjs';
import {ReferralApiService} from '../../../shared/services/referral-api.service';
import {ApiReferral, ApiReferrer, REFERRAL_STATUS_KEYS, REFERRAL_STATUS_LABELS} from '../referral-list/referral-list.component';
import {centerText, REF_C} from '../shared/ref-chart.component';
import {cap, fmtDate} from '../shared/ref-utils';

const DAY = 86400000;

interface Stage {
    label: string;
    n: number;
    c: string;
    note: string;
    status: string;
}

interface Todo {
    n: number;
    title: string;
    sub: string;
    bg: string;
    fg: string;
    link: string;
    params: any;
}

interface FeedItem {
    referrer: string;
    referred: string;
    statusKey: string;
    statusLabel: string;
    t: number;
}

const noAxes = {xAxes: [{display: false}], yAxes: [{display: false}]};
const miniOpts = {legend: {display: false}};
const grid = {color: REF_C.line, drawBorder: false, zeroLineColor: REF_C.line};
const noGrid = {display: false, drawBorder: false};
const intTicks = {beginAtZero: true, precision: 0, fontColor: REF_C.muted};

const STATUS_COLORS: { [k: string]: string } = {
    PENDING_REGISTRATION: REF_C.gray,
    REGISTERED: REF_C.blue,
    PENDING: REF_C.amber,
    CONVERTED: REF_C.green,
    CLOSED: REF_C.muted,
    REJECTED: REF_C.red,
    UNDER_REVIEW: REF_C.mid
};

@Component({
    selector: 'app-referral-dashboard',
    templateUrl: './referral-dashboard.component.html'
})
export class ReferralDashboardComponent implements OnInit {
    ranges = [{v: 30, label: 'Last 30 days'}, {v: 90, label: 'Last 90 days'}, {v: 0, label: 'All time'}];
    range = 0;

    loading = false;
    loaded = false;
    all: ApiReferral[] = [];

    stages: Stage[] = [];
    rejected = 0;
    closed = 0;
    kpi: any = {};
    charts: { [k: string]: any } = {};
    todos: Todo[] = [];
    feed: FeedItem[] = [];

    constructor(private referralApi: ReferralApiService, private router: Router) {
    }

    ago(iso: string | number): string {
        if (!iso) {
            return '';
        }
        const d = Math.floor((Date.now() - new Date(iso).getTime()) / DAY);
        return d <= 0 ? 'Today' : d === 1 ? 'Yesterday' : d + ' days ago';
    }

    ngOnInit() {
        this.fetch();
    }

    setRange(v: number) {
        this.range = v;
        this.render();
    }

    openStage(status: string) {
        this.router.navigate(['/referral/referrals'], status ? {queryParams: {status}} : {});
    }

    openTodo(t: Todo) {
        this.router.navigate([t.link], {queryParams: t.params});
    }

    fetch() {
        this.loading = true;
        const perPage = 100;
        this.referralApi.getReferrals({page: 1, per_page: perPage}).subscribe(res => {
            const first: ApiReferral[] = (res && res.data) ? res.data : (Array.isArray(res) ? res : []);
            const lastPage = res && res.last_page ? res.last_page : 1;
            if (lastPage <= 1) {
                this.finishFetch(first);
                return;
            }
            const rest: Observable<any>[] = [];
            for (let p = 2; p <= lastPage; p++) {
                rest.push(this.referralApi.getReferrals({page: p, per_page: perPage}));
            }
            forkJoin(rest).subscribe(pages => {
                this.finishFetch(first.concat(...pages.map(r => (r && r.data) ? r.data : [])));
            }, () => this.finishFetch(first));
        }, () => this.finishFetch([]));
    }

    private finishFetch(all: ApiReferral[]) {
        this.all = all;
        this.loading = false;
        this.loaded = true;
        this.render();
    }

    private inRange(iso: string): boolean {
        if (!this.range) {
            return true;
        }
        return Date.now() - new Date(iso).getTime() <= this.range * DAY;
    }

    private render() {
        const NOW = Date.now();
        const rs = this.all.filter(r => this.inRange(r.created_at));

        /* ---- Pipeline ---- */
        const referred = rs.length;
        const registered = rs.filter(r => !!r.registered_at).length;
        const converted = rs.filter(r => !!r.converted_at).length;
        this.stages = [
            {label: 'Referred', n: referred, c: '#8B87F8', note: 'Sent by partners', status: ''},
            {label: 'Registered', n: registered, c: '#6F6AF6', note: 'Completed registration', status: 'REGISTERED'},
            {label: 'Fee paid', n: converted, c: '#5A55F5', note: 'Converted to admission', status: 'CONVERTED'}
        ];
        this.rejected = rs.filter(r => r.status === 'REJECTED').length;
        this.closed = rs.filter(r => r.status === 'CLOSED').length;

        /* ---- KPI cards ---- */
        const since30 = NOW - 30 * DAY;
        const since60 = NOW - 60 * DAY;
        const refsLast30 = this.all.filter(r => new Date(r.created_at).getTime() > since30);
        const refsPrev30 = this.all.filter(r => {
            const t = new Date(r.created_at).getTime();
            return t <= since30 && t > since60;
        });
        const trend = refsPrev30.length ? Math.round((refsLast30.length - refsPrev30.length) / refsPrev30.length * 100)
            : (refsLast30.length ? 100 : 0);

        const referrerId = (r: ApiReferral) => r.referrer ? r.referrer.id : null;
        const activeReferrerIds = new Set(refsLast30.map(referrerId).filter(id => id != null));
        const allReferrerIds = new Set(this.all.map(referrerId).filter(id => id != null));
        const idleCount = allReferrerIds.size - activeReferrerIds.size;

        const conv = referred ? Math.round(converted / referred * 100) : 0;

        const rewardsInRange = rs.reduce((arr, r) => arr.concat(r.rewards || []), [] as any[]);
        const totalPoints = rewardsInRange.reduce((a, x) => a + (+x.points || 0), 0);
        const byStatus: { [k: string]: number } = {};
        rewardsInRange.forEach(x => {
            const k = (x.status || 'unknown').toLowerCase();
            byStatus[k] = (byStatus[k] || 0) + (+x.points || 0);
        });
        const statusEntries = Object.entries(byStatus).sort((a, b) => b[1] - a[1]);

        this.kpi = {
            active: activeReferrerIds.size,
            activeSub: `${idleCount} inactive of ${allReferrerIds.size} total`,
            refs: refsLast30.length, trend,
            conv, convSub: `${converted} of ${referred} referrals`,
            points: totalPoints.toLocaleString('en-IN'),
            pointsSub: rewardsInRange.length ? `Across ${rewardsInRange.length} reward entries` : 'No rewards recorded yet'
        };

        this.charts.partners = allReferrerIds.size ? {
            type: 'doughnut', plugins: [centerText(allReferrerIds.size, 14)],
            data: {
                labels: ['Active (30d)', 'Inactive'],
                datasets: [{data: [activeReferrerIds.size, idleCount], backgroundColor: [REF_C.accent, REF_C.soft], borderWidth: 0}]
            },
            options: {...miniOpts, cutoutPercentage: 70}
        } : null;

        const days = Array.from({length: 10}, (_, i) => {
            const start = NOW - (10 - i) * 3 * DAY;
            const end = start + 3 * DAY;
            return this.all.filter(r => {
                const t = new Date(r.created_at).getTime();
                return t > start && t <= end;
            }).length;
        });
        this.charts.spark = {
            type: 'line',
            data: {
                labels: days.map((_, i) => fmtDate(NOW - (10 - i) * 3 * DAY + DAY) + ' to ' + fmtDate(NOW - (9 - i) * 3 * DAY)),
                datasets: [{data: days, borderColor: REF_C.accent, backgroundColor: 'rgba(90,85,245,.14)', fill: true, lineTension: .4, pointRadius: 0, borderWidth: 2}]
            },
            options: {...miniOpts, scales: noAxes, tooltips: {callbacks: {label: (it: any) => ` ${it.yLabel} referrals`}}}
        };

        this.charts.conv = {
            type: 'doughnut', plugins: [centerText(conv + '%', 14)],
            data: {labels: ['Fee paid', 'Not yet'], datasets: [{data: [converted, referred - converted], backgroundColor: [REF_C.green, '#E9EBF1'], borderWidth: 0}]},
            options: {...miniOpts, cutoutPercentage: 74, rotation: -Math.PI, circumference: Math.PI}
        };

        const rewColors = [REF_C.green, REF_C.blue, REF_C.amber, REF_C.red, REF_C.mid, REF_C.dark];
        this.charts.rewards = statusEntries.length ? {
            type: 'doughnut',
            data: {
                labels: statusEntries.map(([k]) => cap(k)),
                datasets: [{
                    data: statusEntries.map(([, v]) => v),
                    backgroundColor: statusEntries.map((_, i) => rewColors[i % rewColors.length]),
                    borderWidth: 0
                }]
            },
            options: {
                ...miniOpts, cutoutPercentage: 62,
                tooltips: {callbacks: {label: (it: any, d: any) => ` ${d.labels[it.index]}: ${d.datasets[0].data[it.index].toLocaleString('en-IN')} pts`}}
            }
        } : null;

        /* ---- Monthly referrals vs conversions ---- */
        const months = this.lastMonths(6);
        const mLabels = months.map(d => d.toLocaleDateString('en-IN', {month: 'short'}));
        const sameMonth = (iso: string, d: Date) => {
            if (!iso) {
                return false;
            }
            const x = new Date(iso);
            return x.getMonth() === d.getMonth() && x.getFullYear() === d.getFullYear();
        };
        const mRefs = months.map(d => this.all.filter(r => sameMonth(r.created_at, d)).length);
        const mConverted = months.map(d => this.all.filter(r => sameMonth(r.converted_at, d)).length);
        const mConv = mRefs.map((r, i) => r ? Math.round(mConverted[i] / r * 100) : 0);
        this.charts.monthly = {
            type: 'bar',
            data: {
                labels: mLabels, datasets: [
                    {
                        type: 'line', label: 'Conversion %', data: mConv, borderColor: REF_C.green, backgroundColor: REF_C.green, fill: false,
                        yAxisID: 'y1', lineTension: .35, pointRadius: 4, pointBackgroundColor: '#fff', pointBorderWidth: 2, order: 1
                    },
                    {label: 'Referrals', data: mRefs, backgroundColor: REF_C.soft, maxBarThickness: 28, yAxisID: 'y', order: 2},
                    {label: 'Fee paid', data: mConverted, backgroundColor: REF_C.accent, maxBarThickness: 28, yAxisID: 'y', order: 2}
                ]
            },
            options: {
                legend: {position: 'bottom'},
                tooltips: {
                    mode: 'index', intersect: false,
                    callbacks: {label: (it: any, d: any) => ` ${d.datasets[it.datasetIndex].label}: ${it.yLabel}${d.datasets[it.datasetIndex].yAxisID === 'y1' ? '%' : ''}`}
                },
                scales: {
                    xAxes: [{gridLines: noGrid, ticks: {fontColor: REF_C.muted}}],
                    yAxes: [
                        {id: 'y', gridLines: grid, ticks: intTicks},
                        {id: 'y1', position: 'right', gridLines: noGrid, ticks: {beginAtZero: true, max: 100, fontColor: REF_C.muted, callback: (v: number) => v + '%'}}
                    ]
                }
            }
        };

        /* ---- Status doughnut ---- */
        const stCounts = REFERRAL_STATUS_KEYS.map(k => rs.filter(r => r.status === k).length);
        this.charts.status = {
            type: 'doughnut', plugins: [centerText(rs.length, 24, 'referrals')],
            data: {
                labels: REFERRAL_STATUS_KEYS.map(k => REFERRAL_STATUS_LABELS[k]),
                datasets: [{data: stCounts, backgroundColor: REFERRAL_STATUS_KEYS.map(k => STATUS_COLORS[k]), borderWidth: 2, borderColor: '#fff', hoverBorderWidth: 0}]
            },
            options: {
                cutoutPercentage: 64, legend: {position: 'right'},
                tooltips: {
                    callbacks: {
                        label: (it: any, d: any) => {
                            const v = d.datasets[0].data[it.index];
                            return ` ${d.labels[it.index]}: ${v} (${rs.length ? Math.round(v / rs.length * 100) : 0}%)`;
                        }
                    }
                },
                onClick: (_e: any, els: any[]) => {
                    if (els.length) {
                        this.openStage(REFERRAL_STATUS_KEYS[els[0]._index]);
                    }
                }
            }
        };

        /* ---- Reward points by month ---- */
        const mPoints = months.map(d => this.all.reduce((a, r) =>
            a + (sameMonth(r.created_at, d) ? (r.rewards || []).reduce((s, x) => s + (+x.points || 0), 0) : 0), 0));
        this.charts.payouts = mPoints.some(v => v > 0) ? {
            type: 'bar',
            data: {labels: mLabels, datasets: [{label: 'Reward points issued', data: mPoints, backgroundColor: REF_C.accent, maxBarThickness: 34}]},
            options: {
                legend: {display: false},
                tooltips: {callbacks: {label: (it: any) => ` ${it.yLabel.toLocaleString('en-IN')} pts`}},
                scales: {
                    xAxes: [{gridLines: noGrid, ticks: {fontColor: REF_C.muted}}],
                    yAxes: [{gridLines: grid, ticks: intTicks}]
                }
            }
        } : null;

        /* ---- Top partners ---- */
        const byReferrer = new Map<number, { p: ApiReferrer, refs: number, conv: number }>();
        rs.forEach(r => {
            if (!r.referrer) {
                return;
            }
            const cur = byReferrer.get(r.referrer.id) || {p: r.referrer, refs: 0, conv: 0};
            cur.refs++;
            if (r.converted_at) {
                cur.conv++;
            }
            byReferrer.set(r.referrer.id, cur);
        });
        const ranked = Array.from(byReferrer.values()).sort((a, b) => b.conv - a.conv || b.refs - a.refs).slice(0, 8);
        this.charts.top = ranked.length ? this.hBar(
            ranked.map(x => x.p.name),
            [{label: 'Fee paid', data: ranked.map(x => x.conv), color: REF_C.accent},
                {label: 'Other referrals', data: ranked.map(x => x.refs - x.conv), color: REF_C.soft}]
        ) : null;

        /* ---- To-do ---- */
        const pendingReg = this.all.filter(r => r.status === 'PENDING_REGISTRATION').length;
        const underReview = this.all.filter(r => r.status === 'UNDER_REVIEW').length;
        const stuck = this.all.filter(r => ['PENDING_REGISTRATION', 'UNDER_REVIEW'].includes(r.status)
            && (NOW - new Date(r.created_at).getTime()) > 7 * DAY).length;
        const awaitingReward = this.all.filter(r => r.status === 'CONVERTED' && (!r.rewards || !r.rewards.length)).length;
        this.todos = [
            {
                n: pendingReg, title: 'Referrals pending registration', sub: 'Waiting for the referred student to complete registration',
                bg: 'var(--gray-t)', fg: 'var(--gray-ink)', link: '/referral/referrals', params: {status: 'PENDING_REGISTRATION'}
            },
            {
                n: underReview, title: 'Referrals under review', sub: 'Needs a decision before moving forward',
                bg: 'var(--amber-t)', fg: 'var(--amber)', link: '/referral/referrals', params: {status: 'UNDER_REVIEW'}
            },
            {
                n: stuck, title: 'Referrals stuck for 7+ days', sub: 'No progress since being referred, follow up now',
                bg: 'var(--red-t)', fg: 'var(--red)', link: '/referral/referrals', params: {stuck: 1}
            },
            {
                n: awaitingReward, title: 'Converted referrals with no reward yet', sub: 'Fee paid but no reward recorded',
                bg: 'var(--blue-t)', fg: 'var(--blue)', link: '/referral/referrals', params: {status: 'CONVERTED'}
            }
        ];

        /* ---- Recent activity ---- */
        this.feed = [...this.all]
            .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
            .slice(0, 8)
            .map(r => ({
                referrer: r.referrer ? r.referrer.name : 'An unknown partner',
                referred: r.referred_name || 'a student',
                statusKey: r.status,
                statusLabel: REFERRAL_STATUS_LABELS[r.status] || r.status,
                t: new Date(r.updated_at).getTime()
            }));
    }

    private hBar(labels: string[], sets: { label: string, data: number[], color: string }[], extra: any = {}) {
        return {
            type: 'horizontalBar',
            data: {labels, datasets: sets.map(s => ({label: s.label, data: s.data, backgroundColor: s.color, stack: 's', maxBarThickness: 22}))},
            options: {
                legend: {position: 'bottom'},
                ...extra,
                tooltips: {mode: 'index', intersect: false, ...(extra.tooltips || {})},
                scales: {
                    xAxes: [{stacked: true, gridLines: grid, ticks: intTicks}],
                    yAxes: [{stacked: true, gridLines: noGrid, ticks: {fontColor: REF_C.muted}}]
                }
            }
        };
    }

    private lastMonths(n: number): Date[] {
        const out: Date[] = [];
        for (let i = n - 1; i >= 0; i--) {
            const d = new Date();
            d.setDate(1);
            d.setMonth(d.getMonth() - i);
            out.push(d);
        }
        return out;
    }
}
